import { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FaSortAmountDown, FaSortAmountDownAlt } from 'react-icons/fa';
import {
  faFileExport,
  faPlus,
  faFileExcel,
  faFilePdf,
  faPlane,
  faEye,
  faPencilAlt,
  faTrash,
  faTrashRestore
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useAircraftManagement } from '../../hooks';
import { useAppSelector } from '../../../store/hooks';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import ProfileCompletionModal from '../ProfileCompletionModal/ProfileCompletionModal';
import { useProfileCompletion } from '../../hooks/useProfileCompletion';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Airliner Aircraft Management Component
 * This component is used by individual airlines to manage their own aircraft fleet
 * For admin system-wide aircraft management, see: /src/pages/AdminDashboard/components/AircraftManagement.js
 */
const AircraftManagement = () => {
  const navigate = useNavigate();
  const { aircraft, loadAircraft, removeAircraft, permanentRemoveAircraft, restoreDeletedAircraft, pagination, loading, error } = useAircraftManagement();
  const { user } = useAppSelector((state) => state.auth);
  const { isModalOpen, closeModal, checkProfileCompletion } = useProfileCompletion();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [aircraftToDelete, setAircraftToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('soft'); // 'soft' or 'permanent'
  const [visibleColumns, setVisibleColumns] = useState({
    model: true,
    regNo: true,
    seats: true,
    endurance: true,
    status: true,
    action: true
  });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <span className="text-[#999] text-[13px] align-middle">⇅</span>;
    }
    return sortConfig.direction === 'asc'
      ? <FaSortAmountDownAlt className="text-black inline text-[13px]" />
      : <FaSortAmountDown className="text-black inline text-[13px]" />;
  };

  // Sort aircraft
  const sortedAircraft = useMemo(() => {
    if (!sortConfig.key || !aircraft) return aircraft || [];

    const sorted = [...aircraft].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'index':
          aValue = a.id || 0;
          bValue = b.id || 0;
          break;
        case 'model':
          aValue = a.aircraftmodel
            ? `${a.aircraftmodel.model}/${a.aircraftmodel.type_designator}`.toLowerCase()
            : (a.aircraft_modual || '').toLowerCase();
          bValue = b.aircraftmodel
            ? `${b.aircraftmodel.model}/${b.aircraftmodel.type_designator}`.toLowerCase()
            : (b.aircraft_modual || '').toLowerCase();
          break;
        case 'regNo':
          aValue = (a.registration_number || '').toLowerCase();
          bValue = (b.registration_number || '').toLowerCase();
          break;
        case 'seats':
          aValue = parseInt(a.seating_capacity) || 0;
          bValue = parseInt(b.seating_capacity) || 0;
          break;
        case 'endurance':
          aValue = a.endurance || '';
          bValue = b.endurance || '';
          break;
        case 'status':
          aValue = a.is_draft === 1 ? 'draft' : 'added';
          bValue = b.is_draft === 1 ? 'draft' : 'added';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [aircraft, sortConfig]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (user?.id) {
      loadAircraft({
        userId: user.id,
        page: currentPage,
        limit: recordsPerPage,
        search: debouncedSearchTerm || undefined
      });
    }
  }, [user?.id, currentPage, recordsPerPage, debouncedSearchTerm, loadAircraft]);

  const displayData = sortedAircraft || [];
  const totalRecords = pagination?.totalRecords || 0;
  const totalPages = pagination?.totalPages || 1;
  const startIndex = ((pagination?.currentPage || 1) - 1) * (pagination?.perPage || recordsPerPage);
  const endIndex = Math.min(startIndex + (pagination?.perPage || recordsPerPage), totalRecords);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const handleExport = (format) => {
    // Get visible column keys (exclude 'action')
    const visibleCols = Object.keys(visibleColumns).filter(key => visibleColumns[key] && key !== 'action');

    const labels = {
      model: 'Aircraft Model',
      regNo: 'Reg No.',
      seats: 'Seat(s)',
      endurance: 'Endurance',
      status: 'Status'
    };

    const dataToExport = displayData.map((aircraftItem, index) => {
      const row = { '#': index + 1 };
      visibleCols.forEach(key => {
        let val = '';
        switch (key) {
          case 'model':
            val = aircraftItem.aircraftmodel
              ? `${aircraftItem.aircraftmodel.manaufacturer}/${aircraftItem.aircraftmodel.model}/${aircraftItem.aircraftmodel.type_designator}`
              : aircraftItem.aircraft_modual || '';
            break;
          case 'regNo':
            val = (aircraftItem.registration_number || '').toUpperCase();
            break;
          case 'seats':
            val = aircraftItem.seating_capacity || '';
            break;
          case 'endurance':
            val = aircraftItem.endurance ? new Date(`1970-01-01T${aircraftItem.endurance}`).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '';
            break;
          case 'status':
            val = aircraftItem.is_draft === 1 ? 'Draft' : 'Added';
            break;
          default:
            val = '';
        }
        row[labels[key] || key] = val;
      });
      return row;
    });

    if (format === 'excel') {
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Aircraft");
      const fileName = `aircraft_data_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } else if (format === 'pdf') {
      const doc = new jsPDF('landscape');
      const tableColumn = ["#", ...visibleCols.map(key => labels[key] || key)];
      const tableRows = dataToExport.map(row => Object.values(row));

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [255, 235, 0], textColor: [0, 0, 0] }
      });
      doc.text("Aircraft Management List", 14, 15);
      const fileName = `aircraft_data_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    }

    setShowExportDropdown(false);
  };

  const handleEditAircraft = (aircraftItem) => {
    navigate(`/airliner/edit-aircraft/${aircraftItem.id}`);
  };

  const handleViewAircraft = (aircraftItem) => {
    navigate(`/airliner/aircraft/${aircraftItem.id}`);
  };

  const handleAddFlight = async (aircraftItem) => {
    if (aircraftItem.is_draft === 1) {
      const toast = (await import('react-hot-toast')).default;
      toast.error('This Aircraft is Saved but NOT added. Please edit, enter or review all information and proceed to "Add Aircraft" before adding a Flight.', {
        duration: 6000,
        position: 'top-right',
      });
      return;
    }
    navigate(`/airliner/aircraft/${aircraftItem.id}/add-flight`);
  };

  const handleDeleteAircraft = (aircraftId, isPermanent = false) => {
    setAircraftToDelete(aircraftId);
    setDeleteType(isPermanent ? 'permanent' : 'soft');
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (aircraftToDelete) {
      try {
        if (deleteType === 'permanent') {
          await permanentRemoveAircraft(aircraftToDelete);
          const toast = (await import('react-hot-toast')).default;
          toast.success('Aircraft permanently deleted');
        } else {
          await removeAircraft(aircraftToDelete);
          const toast = (await import('react-hot-toast')).default;
          toast.success('Aircraft deleted successfully');
        }
        setShowDeleteConfirmation(false);
        setAircraftToDelete(null);
        setDeleteType('soft');
      } catch (error) {
        const toast = (await import('react-hot-toast')).default;
        toast.error('Failed to delete aircraft. Please try again.');
      }
    }
  };

  const handleRestoreAircraft = async (aircraftId) => {
    try {
      await restoreDeletedAircraft(aircraftId);
      // Reload aircraft list to show updated state
      if (user?.id) {
        await loadAircraft({
          userId: user.id,
          page: currentPage,
          limit: recordsPerPage,
          search: debouncedSearchTerm || undefined
        });
      }
      const toast = (await import('react-hot-toast')).default;
      toast.success('Aircraft restored successfully');
    } catch (error) {
      const toast = (await import('react-hot-toast')).default;
      toast.error('Failed to restore aircraft. Please try again.');
    }
  };

  const formatEndurance = (endurance) => {
    if (!endurance) return '';
    try {
      const date = new Date(`1970-01-01T${endurance}`);
      return date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return endurance;
    }
  };

  return (
    <div className="p-0 lg:ml-6 bg-jet-gray-100 min-h-screen w-full relative font-['Bahnschrift_Light','Segoe_UI',Tahoma,Geneva,Verdana,sans-serif] flex flex-col">
      <div className="flex-1">
        <div className="py-5 px-[15px] md:px-[30px] mb-0 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-0 bg-transparent">
          <h1 className="m-0 text-lg text-[#333] font-bold mb-0 hidden md:block">Aircraft Management</h1>
          <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center w-full md:w-auto">
            <div className="relative w-full sm:w-auto">
              <button
                className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
                onClick={() => setShowExportDropdown(!showExportDropdown)}
              >
                <FontAwesomeIcon icon={faFileExport} /> Export
              </button>
              {showExportDropdown && (
                <div className="absolute top-full left-0 bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded p-0 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-10 min-w-[150px] mt-[5px] overflow-hidden">
                  <div onClick={() => handleExport('excel')} className="py-3 px-4 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]">
                    <FontAwesomeIcon icon={faFileExcel} /> Excel
                  </div>
                  <div onClick={() => handleExport('pdf')} className="py-3 px-4 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]">
                    <FontAwesomeIcon icon={faFilePdf} /> PDF
                  </div>
                </div>
              )}
            </div>
            <div className="relative w-full sm:w-auto">
              <button
                className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
              >
                Hide/Show Column ▼
              </button>
              {showColumnDropdown && (
                <div className="absolute top-full right-0 bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded py-2.5 px-0 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-10 min-w-[280px] mt-[5px]">
                  {Object.keys(visibleColumns).map(key => (
                    <label
                      key={key}
                      onClick={() => toggleColumnVisibility(key)}
                      className="flex items-center py-2 px-5 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
                    >
                      <span className="inline-flex items-center justify-center">
                        <span className="inline-flex items-center justify-center w-4 h-4 min-w-[16px] border border-[#666] rounded-sm bg-white">
                          {visibleColumns[key] && <span className="text-[#2ecc71] text-[14px] font-bold leading-none">✓</span>}
                        </span>
                      </span>
                      <span className="flex-1 whitespace-nowrap">
                        {key === 'model' && 'Aircraft Model'}
                        {key === 'regNo' && 'Number'}
                        {key === 'seats' && 'Seating Capacity'}
                        {key === 'endurance' && 'Endurance'}
                        {key === 'status' && 'Status'}
                        {key === 'action' && 'Action'}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <button
              className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
              onClick={() => {
                if (checkProfileCompletion()) {
                  navigate('/airliner/add-aircraft');
                }
              }}
            >
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: '3px' }} /> Add Aircraft
            </button>
          </div>
        </div>

        <div className="bg-white m-4 md:m-10">
          <div className="flex justify-between items-center py-[15px] px-[15px] md:px-[30px] bg-transparent flex-col md:flex-row gap-[15px] md:gap-0 items-start md:items-center">
            <div className="flex items-center gap-2 text-[13px] text-[#555] w-full md:w-auto justify-between md:justify-start">
              <span>Display</span>
              <select value={recordsPerPage} onChange={handleRecordsPerPageChange} className="w-[75px] h-[34px] py-1.5 px-3 text-[13px] leading-[1.42857143] text-black bg-white border border-[#ccc] rounded-[5px] shadow-none font-semibold inline-block align-middle my-0 mx-[5px] transition-[border-color] duration-150 focus:outline-none focus:border-[#ccc] focus:shadow-none">
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>Records</span>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 m-0 w-full md:w-auto">
              <div className="flex items-center gap-2.5 w-full sm:w-auto">
                <label className="text-[13px] text-[#555] font-normal">Search</label>
                <input
                  type="text"
                  placeholder=""
                  value={searchTerm}
                  onChange={handleSearch}
                  className="inline-block w-full sm:w-[150px] h-[34px] py-1.5 px-3 text-[13px] leading-[1.42857143] text-[#4e4c4c] bg-white border border-[#ccc] rounded-[5px] shadow-none align-middle appearance-none box-border transition-[border-color,box-shadow] duration-150 font-normal focus:outline-none focus:border-[#ccc] focus:shadow-none placeholder:text-[#999] placeholder:font-normal"
                />
              </div>
              <div className="inline-flex w-full sm:w-auto">
                <button
                  onClick={handlePreviousPage}
                  className="py-[5px] px-4 border border-[#ddd] bg-white rounded-tl-md rounded-bl-md rounded-tr-none rounded-br-none border-r-0 cursor-pointer text-base text-[#aaa] h-[34px] min-w-[80px] sm:min-w-[100px] font-light inline-block align-middle appearance-none box-border transition-all duration-200 leading-[1.5] text-center whitespace-nowrap select-none shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:bg-[#f9f9f9] hover:border-[#ccc] hover:text-[#888] focus:outline-none focus:border-[#ddd] focus:shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  className="py-[5px] px-4 border border-[#ddd] bg-white rounded-tr-md rounded-br-md rounded-tl-none rounded-bl-none cursor-pointer text-base text-[#aaa] h-[34px] min-w-[80px] sm:min-w-[100px] font-light inline-block align-middle appearance-none box-border transition-all duration-200 leading-[1.5] text-center whitespace-nowrap select-none shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:bg-[#f9f9f9] hover:border-[#ccc] hover:text-[#888] focus:outline-none focus:border-[#ddd] focus:shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white mx-[15px] md:mx-[30px] mt-0 mb-[15px] rounded overflow-x-auto shadow-[0_1px_3px_rgba(0,0,0,0.05)] border border-[#e0e0e0]">
            <table className="w-full border-collapse bg-white min-w-[600px]">
              <thead className="bg-white border-2 border-[#e0e0e0]">
                <tr>
                  <th onClick={() => handleSort('index')} className="py-1.5 px-2.5 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap pr-[30px] cursor-pointer hover:bg-[#f0f0f0] transition-colors">
                    <span className="flex items-center gap-1 justify-between w-100"># {renderSortIcon('index')}</span>
                  </th>
                  {visibleColumns.model && <th onClick={() => handleSort('model')} className="py-1.5 px-2.5 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap cursor-pointer relative pr-[30px] hover:bg-[#f0f0f0] transition-colors">
                    <span className="flex items-center gap-1 justify-between w-100">Aircraft Model {renderSortIcon('model')}</span>
                  </th>}
                  {visibleColumns.regNo && <th onClick={() => handleSort('regNo')} className="py-1.5 px-2.5 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap cursor-pointer relative pr-[30px] hover:bg-[#f0f0f0] transition-colors">
                    <span className="flex items-center gap-1 justify-between w-100">Reg No. {renderSortIcon('regNo')}</span>
                  </th>}
                  {visibleColumns.seats && <th onClick={() => handleSort('seats')} className="py-1.5 px-2.5 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap cursor-pointer relative pr-[30px] hover:bg-[#f0f0f0] transition-colors">
                    <span className="flex items-center gap-1 justify-between w-100">Seat(s) {renderSortIcon('seats')}</span>
                  </th>}
                  {visibleColumns.endurance && <th onClick={() => handleSort('endurance')} className="py-1.5 px-2.5 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap cursor-pointer relative pr-[30px] hover:bg-[#f0f0f0] transition-colors">
                    <span className="flex items-center gap-1 justify-between w-100">Endurance {renderSortIcon('endurance')}</span>
                  </th>}
                  {visibleColumns.status && <th onClick={() => handleSort('status')} c>
                    <span className="flex items-center gap-1 justify-between w-100">Status {renderSortIcon('status')}</span>
                  </th>}
                  {visibleColumns.action && <th  className="py-1.5 px-2.5 text-left font-semibold text-black border-2 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap cursor-pointer relative pr-[30px] hover:bg-[#f0f0f0] transition-colors">
                    Action
                  </th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center p-5 text-[#aaa] text-[13px] font-light">
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center p-5 text-red-500 text-[13px] font-light">
                      Error: {error}
                    </td>
                  </tr>
                ) : displayData.length === 0 ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center p-5 text-[#aaa] text-[13px] font-light">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  displayData.map((aircraftItem, index) => (
                    <tr key={aircraftItem.id} className="transition-colors duration-200 hover:bg-[#f9f9f9]">
                      <td className="py-3.5 px-4 border-b text-center border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">{startIndex + index + 1}</td>
                      {visibleColumns.model && <td className="py-3.5 px-4 text-center border-b border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {aircraftItem.aircraftmodel
                          ? `${aircraftItem.aircraftmodel.model}/${aircraftItem.aircraftmodel.type_designator}`
                          : aircraftItem.aircraft_modual || ''
                        }
                      </td>}
                      {visibleColumns.regNo && <td className="py-3.5 px-4 border-b text-center border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {(aircraftItem.registration_number || '').toUpperCase()}
                      </td>}
                      {visibleColumns.seats && <td className="py-3.5 px-4 border-b text-center border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {aircraftItem.seating_capacity}
                      </td>}
                      {visibleColumns.endurance && <td className="py-3.5 px-4 text-center border-b border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {formatEndurance(aircraftItem.endurance)}
                      </td>}
                      {visibleColumns.status && <td className="py-3.5 px-4 text-center border-b border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {aircraftItem.is_draft === 1 ? (
                          <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-orange-500 rounded">Draft</span>
                        ) : (
                          <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-green-500 rounded">Added</span>
                        )}
                      </td>}
                      {visibleColumns.action && <td className="py-3.5 px-4 text-end border-b border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {aircraftItem.deleted_at ? (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => handleRestoreAircraft(aircraftItem.id)}
                              className="w-6 h-6 flex items-center justify-center bg-[#3b5998] text-white rounded hover:bg-[#2d4373] transition-colors"
                              title="Restore Aircraft"
                            >
                              <FontAwesomeIcon icon={faTrashRestore} className="text-[9px]" />
                            </button>
                            <button
                              onClick={() => handleDeleteAircraft(aircraftItem.id, true)}
                              className="w-6 h-6 flex items-center justify-center bg-[#dd4b39] text-white rounded hover:bg-[#c0392b] transition-colors"
                              title="Permanently Delete"
                            >
                              <FontAwesomeIcon icon={faTrash} className="text-[9px]" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => handleAddFlight(aircraftItem)}
                              className={`w-6 h-6 flex items-center justify-center text-white rounded transition-colors ${aircraftItem.is_draft === 1
                                ? 'bg-gray-400'
                                : 'bg-[#3b5998] hover:bg-[#2d4373]'
                                }`}
                              title={aircraftItem.is_draft === 1
                                ? 'This Aircraft is Saved but NOT added. Please edit, enter or review all information and proceed to "Add Aircraft" before adding a Flight.'
                                : 'Add Flight'
                              }
                            >
                              <FontAwesomeIcon icon={faPlane} className="text-[9px]" />
                            </button>
                            <button
                              onClick={() => handleViewAircraft(aircraftItem)}
                              className="w-6 h-6 flex items-center justify-center bg-[#3b5998] text-white rounded hover:bg-[#2d4373] transition-colors"
                              title="Show Aircraft Details"
                            >
                              <FontAwesomeIcon icon={faEye} className="text-[9px]" />
                            </button>
                            {(aircraftItem.is_draft === 1 || Number(aircraftItem.routes_count) === 0) && (
                              <>
                                <button
                                  onClick={() => handleEditAircraft(aircraftItem)}
                                  className="w-6 h-6 flex items-center justify-center bg-[#f4c430] text-black rounded hover:bg-[#e0b020] transition-colors"
                                  title="Edit Aircraft Details"
                                >
                                  <FontAwesomeIcon icon={faPencilAlt} className="text-[9px]" />
                                </button>
                                <button
                                  onClick={() => handleDeleteAircraft(aircraftItem.id, false)}
                                  className="w-6 h-6 flex items-center justify-center bg-[#dd4b39] text-white rounded hover:bg-[#c0392b] transition-colors"
                                  title="Delete Aircraft"
                                >
                                  <FontAwesomeIcon icon={faTrash} className="text-[9px]" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mx-[15px] md:mx-[30px] my-0 py-[15px] px-0 bg-transparent gap-2">
            <div className="text-[13px] text-[#555] text-left whitespace-nowrap">
              Showing {totalRecords === 0 ? 0 : startIndex + 1} to {endIndex} of {totalRecords} entries
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Section */}
      <div className="pt-0 p-[15px] mr-auto ml-auto px-[15px] bg-jet-gray-100 w-full">
        <div className="bg-white p-[10px] md:p-[15px] text-black border-t-0 text-center shadow-none pb-[10px] rounded-[5px] text-[12px] md:text-[14px]">
          Copyright © 2026 Jetscanner360. All rights reserved.
        </div>
      </div>

      {/* Profile Incomplete Modal */}
      <ProfileCompletionModal
        isOpen={isModalOpen}
        onClose={closeModal}
      />

      {/* Delete Confirmation Modal */}
      <UniversalConfirmModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setAircraftToDelete(null);
          setDeleteType('soft');
        }}
        onConfirm={handleConfirmDelete}
        type={deleteType === 'permanent' ? 'permanentDelete' : 'delete'}
        title="Are you sure?"
        message={
          deleteType === 'permanent'
            ? 'You are about to PERMANENTLY DELETE this Aircraft. This action cannot be undone. Click DELETE to proceed or CANCEL to exit.'
            : 'You are about to DELETE this Aircraft. Click DELETE to proceed or CANCEL to exit.'
        }
        theme="modern"
      />
    </div>
  );
};

export default AircraftManagement;
